import { useCallback, useEffect, useRef, memo } from 'react';
import { EventType } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-call/v1/call_pops.pb';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { NotificationQueries } from '@frontend/api-notifications';
import { SoftphoneTypes } from '@frontend/api-softphone';
import { i18next, useTranslation } from '@frontend/i18n';
import { isPhoneNumber } from '@frontend/phone-numbers';
import { usePhotoStore } from '@frontend/photos';
import { CallPopActionHandler, CallPopInterface, useCallPopStateSync, useSubscribeToPopAction } from '@frontend/pop';
import { useScopedAppFlagStore } from '@frontend/scope';
import { phoneNotificationTypes } from '@frontend/settings-routing';
import { shell } from '@frontend/shell-utils';
import {
  isAutoAnswerCall,
  isIntercomCall,
  isOccupiedParkSlot,
  isTerminatedCall,
  useSoftphoneCallActions,
  useSoftphoneCallState,
  useSoftphoneEventSubscription,
  useSoftphoneParkSlots,
  useSoftphoneSettings,
  useSoftphoneRingManager,
} from '@frontend/softphone2';
import TempoTracing from '@frontend/tempo-tracing';
import { sentry } from '@frontend/tracking';
import { PhoneEventV2Payload, useWebsocketEventSubscription } from '@frontend/websocket';
import { useAlert } from '@frontend/design-system';

type CallPopManagerProps = {
  softphoneData: SoftphoneTypes.SoftphoneData | undefined;
  isCallWaitingIndicatorBeepEnabled: boolean;
  hasActiveCalls: boolean;
};

const DEVTOOLS_ID = 'devtools';
export const SoftphoneCallPopManager = memo(function SoftphoneCallPopManager({
  isCallWaitingIndicatorBeepEnabled,
  hasActiveCalls,
  softphoneData,
}: CallPopManagerProps) {
  const { t } = useTranslation('softphone');
  const { handleIncomingRing, handleCompletedRing, handleMissedRing } = useSoftphoneRingManager();
  const calls = useSoftphoneCallState((ctx) => ctx.calls);
  const isDoNotDisturb = useSoftphoneSettings((ctx) => ctx.isDoNotDisturb);
  const answer = useSoftphoneCallActions((ctx) => ctx.answerIncomingCall);
  const reject = useSoftphoneCallActions((ctx) => ctx.endCall);
  const parkSlots = useSoftphoneParkSlots((ctx) => ctx.parkSlotsWithPresence);
  const { addNotification } = useCallPopStateSync();
  const inboundCallWebsocketMessages = useRef<PhoneEventV2Payload[]>([]);
  const currentInboundTrace = useRef<string | undefined>(undefined);
  const nonTerminatedCalls = calls.filter((call) => !isTerminatedCall(call));
  const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasPhoneNumbersNameIdFlag = getFeatureFlagValue('phone-numbers-name-id');

  const { data: notificationSettings } = NotificationQueries.useNotificationSettingsQuery();
  const CallerProfileAutoOpenNotificationId = phoneNotificationTypes.find(
    (notification) => notification.type === 'incoming-call'
  )?.id;

  const autoShowCallerProfile = !!notificationSettings?.find(({ id }) => id === CallerProfileAutoOpenNotificationId)
    ?.sendNotification;

  useSoftphonePopActions();

  useWebsocketEventSubscription('PhoneSystemEventsV2', (payload) => {
    const event = payload.params.event;
    if (event === 'inbound_call') {
      inboundCallWebsocketMessages.current = [payload as PhoneEventV2Payload];
    }
  });

  const getWebsocketData = (phone: string) =>
    new Promise<PhoneEventV2Payload | undefined>((resolve, rej) => {
      let timeout: NodeJS.Timeout | undefined = undefined;
      const interval = setInterval(() => {
        const payload = inboundCallWebsocketMessages.current.find(
          (payload) => payload.params.caller_id_number === phone || phone === DEVTOOLS_ID
        );

        /**
         * Only resolve if there is a payload, if we never resolve here, it will be caught by the timeout
         * and we will reject the promise
         **/
        if (payload) {
          clearInterval(interval);
          clearTimeout(timeout);
          resolve(payload);
        }
      }, 200);

      timeout = setTimeout(() => {
        clearInterval(interval);
        rej(new Error('No payload found'));
      }, 2000);
    }).catch(() => undefined);

  const handleGetPicture = usePhotoStore((state) => state.handleGetPicture);

  useSoftphoneEventSubscription(
    'incoming-call.received',
    async (call) => {
      let traceId: string | undefined;
      try {
        if (!softphoneData) {
          return;
        }

        const otherActiveCalls = nonTerminatedCalls.filter((c) => c.id !== call.id);

        const isParkRingback = () => {
          const occupiedSlots = parkSlots.filter(isOccupiedParkSlot);
          return occupiedSlots.some(
            (slot) =>
              slot.remoteParty.displayName === call.remoteParty.displayName ||
              slot.remoteParty.uri.split('@')[0]?.replace('sip:', '') === call.remoteParty.uri
          );
        };

        const shouldAutoReject =
          (isIntercomCall(call.invitation) || isAutoAnswerCall(call.invitation)) && !!otherActiveCalls.length;
        if (shouldAutoReject) {
          reject(call);
          return;
        }

        const shouldAutoAnswer = isIntercomCall(call.invitation) || isAutoAnswerCall(call.invitation);
        if (shouldAutoAnswer) {
          answer(call);
          return;
        }

        const shouldIgnore = isParkRingback() || isDoNotDisturb;

        if (shouldIgnore) {
          console.info('Not showing callpop because it is a park ringback or doNotDisturb is turned on');
          return;
        }

        const isProbablyExtension = call.remoteParty.uri.length < 5;
        const extension = isProbablyExtension
          ? softphoneData?.extensions.find(
              (ext) => ext.presenceUri === call.remoteParty.uri || `${ext.number}` === call.remoteParty.uri
            )
          : undefined;

        const shouldWaitForWebsocket = !extension;
        const websocketData = await (shouldWaitForWebsocket ? getWebsocketData(call.remoteParty.uri) : undefined);

        let notification: Parameters<typeof addNotification>[0] | undefined;
        if (websocketData) {
          // For events not originating from an extension
          traceId = websocketData.trace_id;
          const contacts = getContacts(websocketData, demoSourceIds);

          const photoData = await handleGetPicture(contacts[0].personId);

          notification = {
            id: call.id,
            timestamp: new Date().toDateString(),
            payload: {
              type: 'softphone',
              autoShowCallerProfile: !!autoShowCallerProfile,
              showPhoneNumberName: hasPhoneNumbersNameIdFlag,
              recipientLocationName: websocketData.params.recipient_location_name,
              callerContext: websocketData.params.caller_context,
              headOfHousehold: websocketData.params.call_pop_head_of_household,
              officeNumberName: websocketData.params.office_number_name ?? '',
              officeNumber: websocketData.params.office_number ?? '',
              contacts,
              photoData,
            },
            trace_id: traceId,
          };
        } else {
          notification = {
            id: call.id,
            timestamp: new Date().toDateString(),
            payload: {
              type: 'softphone',
              autoShowCallerProfile: !!autoShowCallerProfile,
              showPhoneNumberName: hasPhoneNumbersNameIdFlag,
              recipientLocationName: '',
              callerContext: '',
              isCallWaitingIndicatorBeepEnabled,
              hasActiveCalls,
              headOfHousehold: {
                head_of_household_id: '',
                head_of_household_person_id: '',
              },
              officeNumberName: '',
              officeNumber: '',
              contacts: [
                {
                  birthdate: 0,
                  callerName: extension ? extension.name : call.remoteParty.displayName ?? t('Unknown Caller'),
                  callerNumber: extension
                    ? t(`Ext. ${extension.number}`)
                    : isPhoneNumber(call.remoteParty.uri)
                    ? call.remoteParty.uri
                    : '',
                  gender: '',
                  householdId: '',
                  matchedLocationId: '',
                  patientId: '',
                  personId: '',
                  recipientLocationName: '',
                  source: '',
                  pets: [],
                },
              ],
            },
          };
        }

        if (notification) {
          if (!!notification.trace_id) {
            currentInboundTrace.current = notification.trace_id;

            TempoTracing.continueTrace(
              notification.trace_id,
              TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call'),
              {
                parentSpanName: TempoTracing.spanNameGenerators.websocketSpan(),
              }
            );

            TempoTracing.addEvent(
              notification.trace_id,
              TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call'),
              {
                eventMessage: 'Notification Emitted to Notification Renderer',
                eventType: EventType.EVENT_TYPE_INFO,
              }
            );
          }
          addNotification(notification);
        }

        if (notification) {
          handleIncomingRing();
        }

        //we don't need to hang on to these any longer than a few seconds
        inboundCallWebsocketMessages.current = [];
      } catch (err) {
        sentry.error({
          topic: 'notification',
          team: 'fe-phone-config',
          error: err,
          addContext: {
            name: 'notification',
            context: {
              errMessage: 'error during websocket processing in softphone, failed to handle',
            },
          },
        });
        console.error(err);
        if (!!traceId) {
          TempoTracing.addEvent(traceId, TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call'), {
            eventMessage: 'Failure to capture incoming call. Reason: error' + err,
            eventType: EventType.EVENT_TYPE_INFO,
          });
          TempoTracing.endSpan(traceId, TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call'));
        }
      }
    },
    [softphoneData, parkSlots, nonTerminatedCalls, inboundCallWebsocketMessages.current]
  );

  // Need to capture the action that caused the ringing to end
  const ringingEndedTraceHandler = (action: string) => {
    if (!!currentInboundTrace.current) {
      TempoTracing.addEvent(
        currentInboundTrace.current,
        TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call'),
        {
          eventMessage: 'Ringing stopped, removing notification. Reason: ' + action,
          eventType: EventType.EVENT_TYPE_INFO,
        }
      );
      TempoTracing.endSpan(
        currentInboundTrace.current,
        TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call')
      );
    }
    currentInboundTrace.current = undefined;
  };
  // I tried to do: ['incoming-call.answered', 'incoming-call.completed-elsewhere', ...].forEach((callAction) => ... ), but types were not exported in a way that I could make this happen :(
  useSoftphoneEventSubscription(['incoming-call.answered'], (e) => {
    CallPopInterface.dismiss(e.id, false);
    ringingEndedTraceHandler('Call answered.');
  });

  useSoftphoneEventSubscription(['incoming-call.completed-elsewhere'], (e) => {
    handleMissedRing(e.id);
    ringingEndedTraceHandler('Call completed elsewhere.');
  });
  useSoftphoneEventSubscription(['incoming-call.missed'], (e) => {
    handleMissedRing(e.id);
    ringingEndedTraceHandler('Call missed.');
  });
  useSoftphoneEventSubscription(['incoming-call.rejected'], () => {
    ringingEndedTraceHandler('Call rejected.');
  });
  useSoftphoneEventSubscription(['active-call.terminated', 'outgoing-call.canceled'], () => {
    handleCompletedRing();
  });
  useSoftphoneEventSubscription(
    ['incoming-call.completed-elsewhere', 'incoming-call.missed', 'incoming-call.rejected'],
    (e) => {
      CallPopInterface.dismiss(e.id);
    }
  );

  return null;
});

const getContacts = (data: PhoneEventV2Payload, demoSourceIds: string[]) => {
  if (data.params.contact_matches) {
    const filteredContacts = data.params.contact_matches
      ?.filter((contact) => (demoSourceIds.length > 0 ? demoSourceIds.includes(contact.source_id) : true))
      .map((item) => ({
        personId: item.person_id,
        patientId: item.patient_id,
        householdId: item.household_id,
        callerName: `${item.first_name} ${item.last_name}`,
        callerNumber: data.params.caller_id_number,
        recipientLocationName: item.client_location_name,
        gender: item.gender,
        birthdate: item.birthdate.seconds,
        source: item.data_source_name,
        matchedLocationId: item.weave_locations_matched[0]?.location_id ?? '',
        pets: item.pets ?? [],
      }));

    // If no contacts match demo sources but we have contacts, return the first one
    if (filteredContacts.length === 0 && data.params.contact_matches.length > 0) {
      const firstContact = data.params.contact_matches[0];
      return [
        {
          personId: firstContact.person_id,
          patientId: firstContact.patient_id,
          householdId: firstContact.household_id,
          callerName: `${firstContact.first_name} ${firstContact.last_name}`,
          callerNumber: data.params.caller_id_number,
          recipientLocationName: firstContact.client_location_name,
          gender: firstContact.gender,
          birthdate: firstContact.birthdate.seconds,
          source: firstContact.data_source_name,
          matchedLocationId: firstContact.weave_locations_matched[0]?.location_id ?? '',
          pets: firstContact.pets ?? [],
        },
      ];
    }

    return filteredContacts;
  } else {
    return [
      {
        personId: '',
        patientId: '',
        householdId: '',
        callerName: i18next.t('Unknown Caller', { ns: 'softphone' }),
        callerNumber: data.params.caller_id_number,
        recipientLocationName: data.params.recipient_location_name,
        gender: '',
        birthdate: 0,
        source: '',
        matchedLocationId: '',
        pets: [],
      },
    ];
  }
};

const useSoftphonePopActions = () => {
  const { t } = useTranslation('softphone');
  const { handleAnswerRing, handleRejectRing, stopAllRing } = useSoftphoneRingManager();
  const { notifications, removeNotification } = useCallPopStateSync();
  const incomingCalls = useSoftphoneCallState((ctx) => ctx.incomingCalls);
  const answer = useSoftphoneCallActions((ctx) => ctx.answerIncomingCall);
  const reject = useSoftphoneCallActions((ctx) => ctx.endCall);

  //storing this stuff in refs so I don't have to keep re-subscribing to events whenever these change
  const notificationsRef = useRef(notifications);
  const incomingCallsRef = useRef(incomingCalls);
  const answerRef = useRef(answer);
  const rejectRef = useRef(reject);

  const alerts = useAlert();

  const cleanupBrokenCalls = useCallback(
    ({ error, notification, type }: { error: unknown; notification: { id: string }; type: 'answer' | 'reject' }) => {
      stopAllRing();
      removeNotification(notification.id);
      if (error instanceof Error && error.message.includes('Invalid session state Terminated')) {
        alerts.error({
          message: t('Call has already ended.'),
        });
      } else {
        alerts.error({
          message: t(`Failed to ${type} call. The call might have already ended.`),
        });
      }
    },
    [alerts, removeNotification, stopAllRing, t]
  );

  const handleMissingCall = useCallback(
    (notification: { id: string } | undefined, call: any, type: 'answer' | 'reject') => {
      sentry.error({
        topic: 'softphone-call-pop',
        team: 'fe-phone-events',
        error: `Call pop showing but no notification or call object exists - preventing ${type.toUpperCase()} action from firing`,
        addContext: {
          name: 'SoftphoneCallPop',
          context: {
            notification,
            call,
          },
        },
      });

      /**
       * If there's a call pop showing, there *should* be a notification
       */
      if (notification) {
        cleanupBrokenCalls({ error: new Error('No call object found'), notification, type });
      }
    },
    [cleanupBrokenCalls]
  );

  useEffect(() => {
    notificationsRef.current = notifications;
    incomingCallsRef.current = incomingCalls;
    answerRef.current = answer;
    rejectRef.current = reject;
  }, [notifications, incomingCalls, answer, reject]);

  const answerHandler: CallPopActionHandler<'answer'> = useCallback(
    async (data) => {
      const notification = notificationsRef.current.find((notification) => notification.id === data.id);
      const call = incomingCallsRef.current.find((call) => call.id === notification?.id);

      if (notification?.id === DEVTOOLS_ID) {
        handleAnswerRing();
        removeNotification(notification.id);
        return;
      }

      if (!notification || !call) {
        /**
         * If we get into a state where there is no call object, the user will not be able to affect the call pop if we simply return
         * because all call pop state changes are handled downstream of the answer action.
         * So we need to force dismiss the call pop as much as we can.
         *
         * If there's a call pop showing, there *should* be a notification
         */
        handleMissingCall(notification, call, 'answer');
        return;
      }

      if (!!notification?.trace_id) {
        TempoTracing.addEvent(
          notification.trace_id,
          TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call'),
          {
            eventMessage: 'User answered call using softphone',
            eventType: EventType.EVENT_TYPE_INFO,
          }
        );
        TempoTracing.endSpan(
          notification.trace_id,
          TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call')
        );
      }
      // the handle answer ring is handled in endCall, to also account for it in the alternative incoming call overlay
      // handleAnswerRing();
      try {
        await answerRef.current(call);
      } catch (error) {
        cleanupBrokenCalls({ error, notification, type: 'answer' });
      }
    },
    [cleanupBrokenCalls, handleAnswerRing, handleMissingCall, removeNotification]
  );

  const hangupHandler: CallPopActionHandler<'hangup'> = useCallback(
    async (data) => {
      const notification = notificationsRef.current.find((notification) => notification.id === data.id);
      const call = incomingCallsRef.current.find((call) => call.id === notification?.id);

      if (notification?.id === DEVTOOLS_ID) {
        handleRejectRing(notification.id);
        removeNotification(notification.id);
        return;
      }

      if (!notification || !call) {
        /**
         * If we get into a state where there is no call object, the user will not be able to affect the call pop if we simply return
         * because all call pop state changes are handled downstream of the reject action.
         * So we need to force dismiss the call pop as much as we can.
         *
         * If there's a call pop showing, there *should* be a notification
         */
        handleMissingCall(notification, call, 'reject');
        return;
      }

      if (!!notification?.trace_id) {
        TempoTracing.addEvent(
          notification.trace_id,
          TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call'),
          {
            eventMessage: 'User rejected call using softphone',
            eventType: EventType.EVENT_TYPE_INFO,
          }
        );
        TempoTracing.endSpan(
          notification.trace_id,
          TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call')
        );
      }

      try {
        await rejectRef.current(call);
      } catch (error) {
        cleanupBrokenCalls({ error, notification, type: 'reject' });
      }
      // the reject  ring is handled in useAnswerIncomingCall, to also account for it in the alternative incoming call overlay
      // handleAnswerRing();
    },
    [cleanupBrokenCalls, handleMissingCall, handleRejectRing, removeNotification]
  );

  const viewHandler: CallPopActionHandler<'viewed'> = useCallback((data) => {
    const notification = notificationsRef.current.find((notification) => notification.id === data.id);
    if (!!notification?.trace_id) {
      TempoTracing.addEvent(
        notification.trace_id,
        TempoTracing.spanNameGenerators.callPopSoftphoneSpan('inbound_call'),
        {
          eventMessage: 'Notification Rendered and Displayed to User',
          eventType: EventType.EVENT_TYPE_INFO,
        }
      );
    }
  }, []);

  //
  /**
   * If the notifications window is dismissed or broken or closed, we want to remove all our call pops so they stop ringing
   * TODO: this probably make sense to do globally to all notifications. Whenever the notification window closes, we should probably invoke dismiss on all notifications by default
   * but for now, we'll try it here
   */
  useEffect(() => {
    const dismissAll = () => {
      notificationsRef.current.forEach((notification) => {
        handleRejectRing(notification.id);
        removeNotification(notification.id);
      });
    };

    const emptyId = 'notifications-empty-handler';
    shell?.on?.('notifications:empty', dismissAll, emptyId);

    return () => {
      shell?.off?.('notifications:empty', dismissAll, emptyId);
    };
  }, []);

  useSubscribeToPopAction('viewed', viewHandler);
  useSubscribeToPopAction('answer', answerHandler);
  useSubscribeToPopAction('hangup', hangupHandler);
};
